import { cartMutation } from '@theme/cart-mutation';
import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });

  }

  /**
   * Updates a cart line from its stock-bounded quantity dropdown.
   * @param {Event} event
   */
  onQuantityDropdownChange(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    event.preventDefault();
    event.stopPropagation();
    const keepDrawerOpen = this.isDrawer && this.closest('dialog')?.open === true;
    if (keepDrawerOpen) this.#restoreOpenDrawer();
    const line = Number(event.target.dataset.cartLine);
    const quantity = Number(event.target.value);
    if (!Number.isInteger(line) || line < 1 || !Number.isInteger(quantity) || quantity < 1) return;

    this.updateQuantity({ line, quantity, action: 'change' });


  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    // Do not remove even the last row until the server cart and sections agree.
    return this.updateQuantity({ line, quantity: 0, action: 'clear' });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  async updateQuantity(config) {
    const { line, quantity } = config;
    // Server lines are not compact rendered-row indices (e.g. hidden gift wrap).
    const row = this.refs.cartItemRows?.find(row =>
      Number(row.querySelector('[data-cart-line]')?.dataset.cartLine) === Number(line) ||
      row.querySelector('.cart-items__remove')?.getAttribute('on:click') === `/onLineItemRemove/${line}`
    );
    const input = row?.querySelector('[data-cart-line]');
    const token = cartMutation.begin();
    if (!token) {
      const control = input instanceof HTMLSelectElement ? input : input?.querySelector('input, select');
      if (control) control.value = control.dataset.currentQuantity || control.defaultValue;
      control?.closest('sp-cart-quantity')?.sync();
      return false;
    }
    const marker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const keepDrawerOpen = this.isDrawer && this.closest('dialog')?.open === true;
    try {
      if (!row?.dataset.key) throw new Error('Cart line unavailable');
      this.refs.cartTotal?.shimmer();
      row.querySelector('text-component')?.shimmer();
      const cart = await cartMutation.json(Theme.routes.cart_change_url, fetchConfig('json', {
        body: JSON.stringify({ id: row.dataset.key, quantity, sections: token.ids.join(','), sections_url: window.location.pathname }),
      }));
      this.#updateQuantitySelectors(cart);
      await cartMutation.commit(token, cart, 'cart-items-component');
      return true;
    } catch (error) {
      // A failed response may follow a landed change: never replay the mutation.
      try { this.#handleCartError(line, { errors: 'Could not update cart. Refreshing cart…' }); } catch { /* refs may be detached */ }
      await cartMutation.recover(token);
      return false;
    } finally {
      try {
        resetShimmer(this);
        if (keepDrawerOpen) this.#restoreOpenDrawer();
        this.#updateCartQuantitySelectorButtonStates();
      } finally {
        await cartMutation.end(token);
        cartPerformance.measureFromMarker(marker);
      }
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput =
      quantitySelector instanceof HTMLSelectElement
        ? quantitySelector
        : quantitySelector?.querySelector('input, select');

    if (!(quantityInput instanceof HTMLInputElement || quantityInput instanceof HTMLSelectElement)) {
      throw new Error('Quantity control not found');
    }

    if (quantityInput instanceof HTMLSelectElement) {
      quantityInput.value = quantityInput.dataset.currentQuantity || quantityInput.value;
    } else {
      quantityInput.value = quantityInput.defaultValue;
    }

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.detail?.data?.coordinated) { this.#updateCartQuantitySelectorButtonStates(); return; }
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Reopens the cart drawer after native select interaction or section morphing closes it.
   */
  #restoreOpenDrawer() {
    requestAnimationFrame(() => {
      const drawer = document.querySelector('cart-drawer-component');
      const dialog = drawer?.querySelector('dialog');
      if (!(dialog instanceof HTMLDialogElement) || dialog.matches(':modal')) return;

      // Section morphing can preserve the `open` attribute while removing the
      // dialog from the browser's modal top layer. Reset that stale state first.
      if (dialog.open) dialog.close();

      if (drawer && 'open' in drawer && typeof drawer.open === 'function') {
        drawer.open();
      } else {
        dialog.showModal();
      }
    });
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
